// index.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAuthStatus } from './utils/api';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';                 // <-- the theme we created
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ClientExtensionProvider } from './contexts/ClientExtensionContext';
import { TutorialProvider } from './contexts/TutorialContext';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import VerifyEmail from './pages/VerifyEmail';
import UserHome from './pages/UserHome';
import TaskManager from './pages/TaskManager';
//import SchedulePage from './pages/SchedulePage';
import LandingPage from './pages/LandingPage';
import TaskView from './pages/TaskView';
import TimeTrial from './pages/TimeTrial';
import TimeTrialSettings from './pages/TimeTrialSettings';
import GameModes from './pages/GameModes';
import Gallery from './pages/Gallery';
import Interests from './pages/Interests';
import PublicImage from './pages/PublicImage';
import Profile from './pages/Profile';
import AppNavigation from './components/AppNavigation';
import PublicToolsRouter from './components/PublicToolsRouter';
import AboutDopatize from './pages/About';
import Feedback from './pages/Feedback';
import Friends from './pages/Friends';
import DaySchedule from './pages/DailySchedule';
import UnsubscribePage from './pages/UnsubscribePage';
import TutorialBuilder from './pages/TutorialBuilder';
import Categories from './pages/Categories';
import Events from './pages/Events';
import EventEditor from './pages/EventEditor';
import PublicEvent from './pages/PublicEvent';
import RunningTasksMockups from './pages/RunningTasksMockups';
import { pageview } from './utils/analytics';
import { syncPushRegistration } from './utils/firebaseNotifications';
import { sanitizeStripeReturnUrl } from './utils/billingCheckout';

sanitizeStripeReturnUrl();

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
};
const ProtectedRoute = ({ children, requiredCapability = null }) => {
  const [state, setState] = useState({ loading: true, authenticated: false, allowed: false });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getAuthStatus();
        if (alive) {
          const authenticated = !!res?.authenticated;
          const allowed = authenticated && (
            !requiredCapability || res?.capabilities?.[requiredCapability] === true
          );
          setState({ loading: false, authenticated, allowed });
          if (authenticated) {
            syncPushRegistration().catch((error) => {
              console.error('Unable to refresh push registration:', error);
            });
          }
        }
      } catch (_) {
        // if /api/me somehow fails, treat as unauthenticated
        if (alive) setState({ loading: false, authenticated: false, allowed: false });
      }
    })();
    return () => { alive = false; };
  }, [requiredCapability]);
  
  if (state.loading) return <div data-app-page-loading="true" />;
  if (!state.authenticated) return <Navigate to="/" replace />; // LandingPage
  if (!state.allowed) return <Navigate to="/user-home" replace />;
  return <div data-app-page-ready="true" style={{ display: 'contents' }}>{children}</div>;
};

const AssistantRoute = () => {
  const location = useLocation();
  return <Navigate to="/user-home" replace state={{ openAssistant: true, assistantConversationId: new URLSearchParams(location.search).get('chat') }} />;
};

const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />                          {/* resets & applies dark bg */}
    <BrowserRouter>
      <Analytics />
      <PublicToolsRouter>
        <NotificationsProvider>
        <ClientExtensionProvider>
          <TutorialProvider>
            <AppNavigation />
            <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/user-home" element={<ProtectedRoute><UserHome /></ProtectedRoute>} />
          <Route path="/assistant" element={<ProtectedRoute><AssistantRoute /></ProtectedRoute>} />
          <Route path="/time-trial" element={<ProtectedRoute><TimeTrial /></ProtectedRoute>} />
          <Route path="/time-trial-settings" element={<ProtectedRoute><TimeTrialSettings /></ProtectedRoute>} />
          <Route path="/game-modes" element={<ProtectedRoute><GameModes /></ProtectedRoute>} />
          <Route path="/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
          <Route path="/:userName/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
          <Route path="/:userName/gallary" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
          <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
          <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
          <Route path="/interests" element={<ProtectedRoute><Interests /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><Categories /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
          <Route path="/event" element={<ProtectedRoute><EventEditor /></ProtectedRoute>} />
          <Route path="/event/:eventId" element={<ProtectedRoute><EventEditor /></ProtectedRoute>} />
          <Route path="/public/event/:token" element={<PublicEvent />} />
          <Route path="/running-task-mockups" element={<RunningTasksMockups />} />
          <Route path="/public/image/:user_name/:image_id" element={<PublicImage />} />
          <Route path="/task-view" element={<ProtectedRoute><TaskView /></ProtectedRoute>} />
          <Route path="/task" element={<ProtectedRoute><TaskManager /></ProtectedRoute>} />
          <Route path="/task/:taskId" element={<ProtectedRoute><TaskManager /></ProtectedRoute>} />
          <Route path="/tutorial-builder" element={(
            <ProtectedRoute requiredCapability="tutorial_builder"><TutorialBuilder /></ProtectedRoute>
          )} />
          {/*<Route path="/schedule" element={<SchedulePage />} />*/}

          {/* temporary backward-compat routes */}
          <Route path="/about" element={<AboutDopatize />} />
          <Route path="/add-task" element={<TaskManager />} />
          <Route path="/edit-task/:taskId" element={<TaskManager />} />
          <Route path="schedule" element={<DaySchedule />} />
          <Route path="make-schedule" element={<Navigate to="/categories" replace />} />
            </Routes>
          </TutorialProvider>
        </ClientExtensionProvider>
        </NotificationsProvider>
      </PublicToolsRouter>
    </BrowserRouter>
  </ThemeProvider>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
